import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Legend, FunnelChart, Funnel, LabelList,
} from "recharts";
import { Loader2, Download, Printer, Trophy, Flame, Target as TargetIcon, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useDivision } from "@/context/DivisionContext";
import { formatINR, formatINRShort } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PIE_COLORS = ["#f04935", "#231f20", "#F4A261", "#6366F1", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981"];
const TEMP_COLORS = { Hot: "#EF4444", Warm: "#F59E0B", Cold: "#0EA5E9", None: "#CBD5E1" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${r[h]}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
}

const Stat = ({ label, value, accent, sub }) => (
  <Card className="p-4" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className="font-heading text-xl font-bold mt-1" style={accent ? { color: accent } : {}}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </Card>
);

function ZoneBranch({ data }) {
  const [expanded, setExpanded] = useState({});
  const zones = data?.zones || [];
  const totals = data?.totals || {};
  const toggle = (z) => setExpanded((e) => ({ ...e, [z]: !e[z] }));
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Stat label="Zones" value={zones.length} />
        <Stat label="Total Booking" value={formatINRShort(totals.booking)} accent="#231f20" sub={`${totals.count ?? 0} deals`} />
        <Stat label="Total Won" value={formatINRShort(totals.won)} accent="#10B981" />
        <Stat label="Avg / Zone" value={formatINRShort(zones.length ? (totals.booking || 0) / zones.length : 0)} />
      </div>
      {zones.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400">No deals to summarise yet. Assign dealers (with zone/branch) to deals to populate this view.</Card>
      ) : (
        <>
          <Card className="p-5 mb-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand" /> Booking vs Won by Zone</h3>
            <ResponsiveContainer width="100%" height={Math.max(220, zones.length * 46)}>
              <BarChart data={zones} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                <XAxis type="number" tickFormatter={(v) => formatINRShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="zone" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={110} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="booking" name="Booking" fill="#231f20" radius={[0, 4, 4, 0]} />
                <Bar dataKey="won" name="Won" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <div className="space-y-2">
            {zones.map((z) => (
              <Card key={z.zone} className="overflow-hidden" data-testid={`zone-row-${z.zone}`}>
                <button onClick={() => toggle(z.zone)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`zone-toggle-${z.zone}`}>
                  <div className="flex items-center gap-2">
                    {expanded[z.zone] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <span className="font-heading font-semibold text-sm text-slate-800">{z.zone}</span>
                    <span className="text-xs text-slate-400">· {z.count} deals · {z.branches.length} branches</span>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-right"><p className="text-[10px] uppercase text-slate-400">Booking</p><p className="font-mono font-semibold text-sm">{formatINRShort(z.booking)}</p></div>
                    <div className="text-right"><p className="text-[10px] uppercase text-slate-400">Won</p><p className="font-mono font-semibold text-sm text-emerald-600">{formatINRShort(z.won)}</p></div>
                  </div>
                </button>
                {expanded[z.zone] && (
                  <div className="border-t border-slate-100 bg-slate-50/50">
                    <Table>
                      <TableHeader><TableRow>{["Branch", "Deals", "Booking", "Won"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-400 h-9">{h}</TableHead>)}</TableRow></TableHeader>
                      <TableBody>
                        {z.branches.map((b) => (
                          <TableRow key={b.branch} data-testid={`branch-row-${z.zone}-${b.branch}`}>
                            <TableCell className="font-medium text-slate-700">{b.branch}</TableCell>
                            <TableCell className="text-slate-600">{b.count}</TableCell>
                            <TableCell className="font-mono">{formatINRShort(b.booking)}</TableCell>
                            <TableCell className="font-mono text-emerald-600">{formatINRShort(b.won)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Reports() {
  const { divisionParam } = useDivision();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const { data: dash, isLoading } = useQuery({
    queryKey: ["dashboard", divisionParam, year, month],
    queryFn: async () => (await api.get("/dashboard", { params: { division_id: divisionParam } })).data,
  });
  const ss = useQuery({ queryKey: ["an-sales", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/sales-summary", { params: { division_id: divisionParam, year, month } })).data }).data;
  const fn = useQuery({ queryKey: ["an-funnel", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/funnel", { params: { division_id: divisionParam } })).data }).data;
  const wl = useQuery({ queryKey: ["an-winloss", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/win-loss", { params: { division_id: divisionParam, year } })).data }).data;
  const hc = useQuery({ queryKey: ["an-health", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/health-cards", { params: { division_id: divisionParam, year, month } })).data }).data || [];
  const po = useQuery({ queryKey: ["an-pending", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/pending-orders", { params: { division_id: divisionParam } })).data }).data;
  const col = useQuery({ queryKey: ["an-collection", divisionParam, year, month], queryFn: async () => (await api.get("/analytics/collection", { params: { division_id: divisionParam } })).data }).data;
  const zs = useQuery({ queryKey: ["an-zone", divisionParam], queryFn: async () => (await api.get("/analytics/zone-summary", { params: { division_id: divisionParam } })).data }).data;

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const k = dash?.kpis || {};
  const trend = dash?.leads_trend || [];
  const leaderboard = dash?.leaderboard || [];
  const lostReasons = Object.entries(wl?.lost_reasons || {}).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Sales summary, funnel, win/loss, team health, orders & collection.">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="report-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[100px]" data-testid="report-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" className="gap-1.5" onClick={() => window.print()} data-testid="export-pdf-btn"><Printer className="h-4 w-4" /> Print</Button>
      </PageHeader>

      <Tabs defaultValue="summary">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="summary" data-testid="tab-summary">Sales Summary</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="tab-funnel">Funnel</TabsTrigger>
          <TabsTrigger value="winloss" data-testid="tab-winloss">Win / Loss</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team Health</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders & Collection</TabsTrigger>
          <TabsTrigger value="zone" data-testid="tab-zone">Zone / Branch</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
        </TabsList>

        {/* SALES SUMMARY */}
        <TabsContent value="summary">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat label="YTD Target" value={formatINRShort(ss?.ytd?.target)} />
            <Stat label="YTD Booking" value={formatINRShort(ss?.ytd?.booking)} accent="#f04935" sub={`${ss?.ytd?.achieved_pct ?? 0}% achieved`} />
            <Stat label={`${MONTHS[month - 1]} Target`} value={formatINRShort(ss?.month_data?.target)} />
            <Stat label={`${MONTHS[month - 1]} Booking`} value={formatINRShort(ss?.month_data?.booking)} accent="#10B981" sub={`${ss?.month_data?.achieved_pct ?? 0}% achieved`} />
          </div>
          <Card className="p-5 mb-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4 flex items-center gap-1.5"><TargetIcon className="h-4 w-4 text-brand" /> Target vs Booking by Salesperson (YTD)</h3>
            {(ss?.by_salesperson || []).length ? (
              <ResponsiveContainer width="100%" height={Math.max(220, (ss.by_salesperson.length) * 46)}>
                <BarChart data={ss.by_salesperson} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                  <XAxis type="number" tickFormatter={(v) => formatINRShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="target_ytd" name="Target" fill="#CBD5E1" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="booking_ytd" name="Booking" fill="#f04935" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-6">No targets set yet. Add targets from the Targets page.</p>}
          </Card>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  {["Salesperson", "YTD Target", "YTD Booking", "Achieved", `${MONTHS[month - 1]} Target`, `${MONTHS[month - 1]} Booking`].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {(ss?.by_salesperson || []).map((s, i) => (
                    <TableRow key={`${s.name}-${i}`} data-testid={`summary-row-${i}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono">{formatINRShort(s.target_ytd)}</TableCell>
                      <TableCell className="font-mono font-semibold">{formatINRShort(s.booking_ytd)}</TableCell>
                      <TableCell><span className={`font-semibold ${s.achieved_pct >= 100 ? "text-emerald-600" : s.achieved_pct >= 60 ? "text-amber-600" : "text-red-600"}`}>{s.achieved_pct}%</span></TableCell>
                      <TableCell className="font-mono">{formatINRShort(s.target_month)}</TableCell>
                      <TableCell className="font-mono">{formatINRShort(s.booking_month)}</TableCell>
                    </TableRow>
                  ))}
                  {!(ss?.by_salesperson || []).length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No data.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* FUNNEL */}
        <TabsContent value="funnel">
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Lead Funnel (count)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <FunnelChart>
                  <Tooltip />
                  <Funnel dataKey="count" data={fn?.lead_funnel || []} isAnimationActive>
                    <LabelList position="right" fill="#334155" stroke="none" dataKey="stage" fontSize={11} />
                    <LabelList position="left" fill="#64748b" stroke="none" dataKey="count" fontSize={11} />
                    {(fn?.lead_funnel || []).map((e, i) => <Cell key={e.stage || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Opportunity Pipeline (count + value)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={fn?.opp_funnel || []} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                  <XAxis type="number" tickFormatter={(v) => formatINRShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip formatter={(v, n) => n === "value" ? formatINR(v) : v} />
                  <Bar dataKey="value" name="value" radius={[0, 4, 4, 0]} fill="#231f20">
                    <LabelList dataKey="count" position="insideRight" fill="#fff" fontSize={10} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4 flex items-center gap-1.5"><Flame className="h-4 w-4 text-red-500" /> Lead Temperature</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={Object.entries(fn?.lead_temperature || {}).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={70} label>
                    {Object.keys(fn?.lead_temperature || {}).map((t, i) => <Cell key={t} fill={TEMP_COLORS[t] || PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip /><Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4 flex items-center gap-1.5"><Flame className="h-4 w-4 text-amber-500" /> Opportunity Temperature (value)</h3>
              <div className="space-y-3 pt-2">
                {Object.entries(fn?.opp_temperature || {}).map(([t, v]) => (
                  <div key={t} data-testid={`opp-temp-${t}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: TEMP_COLORS[t] }} />{t}</span><span className="font-mono text-slate-600">{formatINRShort(v.value)} · {v.count}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ background: TEMP_COLORS[t], width: `${Math.min(100, (v.value / (Math.max(...Object.values(fn?.opp_temperature || {}).map((x) => x.value), 1))) * 100)}%` }} /></div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* WIN / LOSS */}
        <TabsContent value="winloss">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat label="Won" value={wl?.won_count ?? 0} accent="#10B981" sub={formatINRShort(wl?.won_value)} />
            <Stat label="Lost" value={wl?.lost_count ?? 0} accent="#EF4444" sub={formatINRShort(wl?.lost_value)} />
            <Stat label="Win Rate" value={`${wl?.win_rate ?? 0}%`} />
            <Stat label="Lost Reasons" value={lostReasons.length} />
          </div>
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Lost Reasons Breakdown</h3>
            {lostReasons.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={lostReasons} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={150} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#f04935" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-8">No lost-reason data yet.</p>}
          </Card>
        </TabsContent>

        {/* TEAM HEALTH */}
        <TabsContent value="team">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hc.map((c) => (
              <Card key={c.id} className="p-5" data-testid={`health-card-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div><p className="font-heading font-bold text-slate-900">{c.name}</p><p className="text-xs text-slate-400 capitalize">{c.role.replace(/_/g, " ")}</p></div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${c.achieved_pct >= 100 ? "bg-emerald-100 text-emerald-700" : c.achieved_pct >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{c.achieved_pct}%</span>
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{formatINRShort(c.booking_month)} booked</span><span>{formatINRShort(c.target_month)} target</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${Math.min(100, c.achieved_pct)}%` }} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-50 rounded-md p-2"><p className="text-[11px] text-slate-400">Open Leads</p><p className="font-semibold">{c.open_leads}</p></div>
                  <div className="bg-slate-50 rounded-md p-2"><p className="text-[11px] text-slate-400">Active Deals</p><p className="font-semibold">{c.active_deals}</p></div>
                  <div className="bg-slate-50 rounded-md p-2"><p className="text-[11px] text-slate-400">Win Rate</p><p className="font-semibold">{c.win_rate}%</p></div>
                  <div className={`rounded-md p-2 ${c.sla_breaches ? "bg-red-50" : "bg-slate-50"}`}><p className="text-[11px] text-slate-400">SLA Breaches</p><p className={`font-semibold ${c.sla_breaches ? "text-red-600" : ""}`}>{c.sla_breaches}</p></div>
                </div>
                <p className="text-xs text-slate-400 mt-3">Pipeline: <span className="font-mono text-slate-600">{formatINRShort(c.pipeline_value)}</span></p>
              </Card>
            ))}
            {!hc.length && <p className="text-sm text-slate-400 col-span-full text-center py-10">No team members found.</p>}
          </div>
        </TabsContent>

        {/* ORDERS & COLLECTION */}
        <TabsContent value="orders">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat label="Pending Orders" value={formatINRShort(po?.total_pending)} accent="#f04935" sub={`${po?.count ?? 0} orders`} />
            <Stat label="Total Billed" value={formatINRShort(col?.billed_total)} />
            <Stat label="Collected" value={formatINRShort(col?.collected_total)} accent="#10B981" />
            <Stat label="Outstanding" value={formatINRShort(col?.outstanding_total)} accent="#EF4444" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Pending Orders by Billing Month</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={po?.by_month || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => formatINRShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Bar dataKey="pending" radius={[4, 4, 0, 0]} fill="#f04935" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Billed vs Collected by Month</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={col?.by_month || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => formatINRShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => formatINR(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="billed" name="Billed" radius={[4, 4, 0, 0]} fill="#231f20" />
                  <Bar dataKey="collected" name="Collected" radius={[4, 4, 0, 0]} fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card className="overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-heading font-semibold text-sm text-slate-800">Pending Orders</h3>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportCSV((po?.items || []).map((i) => ({ deal: i.name, company: i.company, booked: i.booked, billed: i.billed, pending: i.pending, month: i.expected_billing_month })), "pending_orders.csv")} data-testid="export-pending-btn"><Download className="h-4 w-4" /> CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">{["Deal", "Company", "Booked", "Billed", "Pending", "Billing Month", "Owner"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {(po?.items || []).map((i) => (
                    <TableRow key={i.id} data-testid={`pending-row-${i.id}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="text-slate-600">{i.company}</TableCell>
                      <TableCell className="font-mono">{formatINRShort(i.booked)}</TableCell>
                      <TableCell className="font-mono">{formatINRShort(i.billed)}</TableCell>
                      <TableCell className="font-mono font-semibold text-red-600">{formatINRShort(i.pending)}</TableCell>
                      <TableCell className="text-slate-500">{i.expected_billing_month}</TableCell>
                      <TableCell className="text-slate-500">{i.owner_name}</TableCell>
                    </TableRow>
                  ))}
                  {!(po?.items || []).length && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No pending orders. Win deals and add billing details.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ZONE / BRANCH */}
        <TabsContent value="zone">
          <ZoneBranch data={zs} />
        </TabsContent>

        {/* OVERVIEW (legacy) */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">New Leads Trend (14 days)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trend} margin={{ left: -20 }}>
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.3} /><stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={1} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip /><Area type="monotone" dataKey="count" stroke="#0EA5E9" strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Salesperson Leaderboard</h3>
              <div className="space-y-2">
                {leaderboard.map((l, i) => (
                  <div key={`${l.name}-${i}`} className="flex items-center justify-between p-2.5 rounded-md bg-slate-50" data-testid={`leaderboard-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>{i + 1}</span>
                      <div><p className="text-sm font-medium">{l.name}</p><p className="text-xs text-slate-400">{l.deals} deals</p></div>
                    </div>
                    <span className="font-mono font-semibold text-sm">{formatINRShort(l.won_value)}</span>
                  </div>
                ))}
                {leaderboard.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data yet.</p>}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
